import React, { useState, useEffect } from 'react';

import { Label } from './label';
import { Checkbox } from './checkbox';
import { Button } from './button';
import { FilterIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { DebouncedInput } from './debounced-input';
import { EnumFilterComponent, EnumFilter } from './filters-enum';

//#region Filter Types

export type BaseFilter = {
  key: string;
  label: string;
  isMainFilter?: boolean;
};

export type StringFilter = BaseFilter & {
  type: 'string';
  value?: string;
};

export type NumberFilter = BaseFilter & {
  type: 'number';
  value?: number;
  minValue?: number;
  maxValue?: number;
};

export type BooleanFilter = BaseFilter & {
  type: 'boolean';
  value?: boolean;
};

export type Filter = StringFilter | NumberFilter | BooleanFilter | EnumFilter;

//#endregion

//#region Filter Type Components

type StringFilterComponentProps = {
  filter: StringFilter;
  onChange: (value: string | undefined) => void;
};

export const StringFilterComponent = ({
  filter,
  onChange,
}: StringFilterComponentProps): React.JSX.Element => {
  return (
    <DebouncedInput
      label={filter.label}
      name={filter.label}
      value={filter.value ?? ''}
      type="text"
      onDebouncedChange={(val: string): void => {
        onChange(val === '' ? undefined : val);
      }}
      containerClassName=""
      inputClassName="rounded-full border-gray-300 focus:border-primary focus:ring-primary/10 focus:shadow-sm focus:outline-none focus:ring-2"
      showFloatingLabel={filter.isMainFilter ? false : true}
      placeholder={
        filter.isMainFilter ? `Search ${filter.label.toLowerCase()}...` : ''
      }
    />
  );
};

type NumberFilterComponentProps = {
  filter: NumberFilter;
  onChange: (value: number | undefined) => void;
};

export const NumberFilterComponent = ({
  filter,
  onChange,
}: NumberFilterComponentProps): React.JSX.Element => {
  return (
    <DebouncedInput
      label={filter.label}
      name={filter.label}
      value={filter.value !== undefined ? filter.value.toString() : ''}
      type="number"
      min={filter.minValue}
      max={filter.maxValue}
      onDebouncedChange={(val: string): void => {
        const parsed = val === '' ? undefined : Number(val);
        onChange(parsed);
      }}
    />
  );
};

type BooleanFilterComponentProps = {
  filter: BooleanFilter;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
};

export const BooleanFilterComponent = ({
  filter,
  value,
  onChange,
}: BooleanFilterComponentProps): React.JSX.Element => {
  const booleanValue =
    typeof value === 'string' ? value === 'true' : Boolean(value);
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={filter.key} className="text-gray-500">
        {filter.label}
      </Label>
      <Checkbox
        id={filter.key}
        checked={booleanValue}
        onCheckedChange={(checked: boolean): void => {
          onChange(checked);
        }}
        className="h-6 w-6 rounded-sm border-gray-400"
      />
    </div>
  );
};

//#endregion

//#region Main ViewFilters Component

type ViewFiltersProps = {
  filters: Filter[];
  onFilterChange: (
    newSearch: Record<string, string | number | boolean | undefined>,
  ) => void;
  /** Whether the filters are being displayed in a mobile-optimized layout */
  mobileView?: boolean;
};

export function ViewFilters({
  filters,
  onFilterChange,
  mobileView = false,
}: ViewFiltersProps): React.JSX.Element {
  const [filterState, setFilterState] = useState<
    Record<string, string | number | boolean | undefined>
  >(() => {
    const initialState: Record<string, string | number | boolean | undefined> =
      {};
    filters.forEach((filter) => {
      initialState[filter.key] = filter.value;
    });
    return initialState;
  });

  useEffect((): void => {
    const newState: Record<string, string | number | boolean | undefined> = {};
    filters.forEach((filter) => {
      newState[filter.key] = filter.value;
    });
    setFilterState(newState);
  }, [filters]);

  const handleFilterChange = (
    key: string,
    value: string | number | boolean | undefined,
  ): void => {
    const newState = { ...filterState, [key]: value };
    setFilterState(newState);
    onFilterChange({ [key]: value });
  };

  const renderFilter = (filter: Filter): React.JSX.Element | null => {
    const { key, label, type } = filter;
    const value = filterState[key];
    if (!label) {
      return null;
    }

    switch (type) {
      case 'number':
        return (
          <NumberFilterComponent
            key={key}
            filter={filter}
            onChange={(val: number | undefined): void => {
              handleFilterChange(key, val);
            }}
          />
        );
      case 'string':
        return (
          <StringFilterComponent
            key={key}
            filter={filter}
            onChange={(val: string | undefined): void => {
              handleFilterChange(key, val);
            }}
          />
        );
      case 'boolean':
        return (
          <BooleanFilterComponent
            key={key}
            filter={filter}
            value={value as boolean}
            onChange={(val: boolean): void => {
              handleFilterChange(key, val);
            }}
          />
        );
      case 'enum':
        return (
          <EnumFilterComponent
            key={key}
            filter={filter}
            value={value as string}
            onChange={(val: string | undefined): void => {
              handleFilterChange(key, val);
            }}
          />
        );
      default:
        return null;
    }
  };

  const mainFilters: Filter[] = filters.filter(
    (filter) => filter.isMainFilter === true,
  );
  const additionalFilters: Filter[] = filters.filter(
    (filter) => filter.isMainFilter !== true,
  );

  return (
    <div className={`relative ${mobileView ? 'w-full max-w-md' : ''}`}>
      {/* Mobile view when mobileView prop is true - WhatsApp-like search bar */}
      {mobileView ? (
        <div className="w-full">
          <div className="flex items-center">
            {/* WhatsApp style search bar */}
            <div className="flex w-full items-center gap-2 rounded-full border border-gray-200 bg-white shadow-sm transition-all focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-200 hover:border-gray-300">
              {/* If we have a main filter that's a search field, render it in the search bar */}
              {mainFilters.length === 1 && mainFilters[0].type === 'string' ? (
                <StringFilterComponent
                  key={mainFilters[0].key}
                  filter={{
                    ...mainFilters[0],
                    label: '', // Remove label for cleaner WhatsApp style
                  }}
                  onChange={(val: string | undefined): void => {
                    handleFilterChange(mainFilters[0].key, val);
                  }}
                />
              ) : null}

              {/* Only show filter button if we have additional filters */}
              {additionalFilters.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 rounded-full p-0 hover:bg-gray-100"
                    >
                      <FilterIcon className="h-3.5 w-3.5 text-gray-400" />
                      {/* Small indicator dot when filters are active */}
                      {Object.values(filterState).some(
                        (v) => v !== undefined,
                      ) && (
                        <span className="absolute right-0 top-0 h-2 w-2 rounded-full bg-blue-500"></span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[calc(100vw-2rem)] max-w-sm p-4"
                    align="end"
                    sideOffset={8}
                  >
                    <div className="grid gap-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Filters</h4>
                        {/* Clear all button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-blue-600"
                          onClick={() => {
                            // Clear all filters
                            const newState: Record<
                              string,
                              string | number | boolean | undefined
                            > = {};
                            filters.forEach((filter) => {
                              newState[filter.key] = undefined;
                            });
                            setFilterState(newState);

                            // Notify parent
                            filters.forEach((filter) => {
                              onFilterChange({ [filter.key]: undefined });
                            });
                          }}
                        >
                          Clear all
                        </Button>
                      </div>

                      {/* All filters in one section */}
                      <div className="space-y-3">
                        {filters.map(
                          (filter: Filter): React.JSX.Element | null =>
                            renderFilter(filter),
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Standard layout (non-mobile view) */}
          <div className="flex flex-row justify-end">
            {/* Mobile: visible on small screens, hidden on md and larger */}
            <div className="right-0 block self-end md:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost">
                    <FilterIcon className="mr-2 h-4 w-4" />
                    Filters
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" align="end">
                  <div className="grid gap-4">
                    {filters.map((filter: Filter): React.JSX.Element | null =>
                      renderFilter(filter),
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Desktop: hidden on small screens, visible on md and larger */}
          <div className="hidden flex-wrap gap-5 md:flex">
            {mainFilters.map((filter: Filter): React.JSX.Element | null =>
              renderFilter(filter),
            )}
            {additionalFilters.length > 0 && (
              <div className="absolute right-0">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost">
                      <FilterIcon className="mr-2 h-4 w-4" />
                      More Filters
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="end">
                    <div className="grid gap-4">
                      {additionalFilters.map(
                        (filter: Filter): React.JSX.Element | null =>
                          renderFilter(filter),
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

//#endregion
